import React, { useState, useEffect } from 'react';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { CustomLink } from '../../components/CustomLink';
import { Button } from '../../components/Button';
import { Lock, ArrowLeft, AlertCircle, Check } from 'lucide-react';
import { authService } from '../../services/authService';
import { useSearchParams } from 'react-router-dom';

function ResetPassword() {
  const navigate = useCustomNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    token: searchParams.get('token') || '',
    newPassword: '',
    confirmPassword: ''
  });
  
  useEffect(() => {
    if (!formData.email || !formData.token) {
      setFormError('Неверная ссылка для сброса пароля. Пожалуйста, проверьте ссылку или запросите сброс пароля снова.');
    }
  }, [formData.email, formData.token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Проверка паролей
    if (formData.newPassword !== formData.confirmPassword) {
      setFormError('Пароли не совпадают');
      return;
    }
    
    if (formData.newPassword.length < 8) {
      setFormError('Пароль должен содержать не менее 8 символов');
      return;
    }
    
    setIsLoading(true);
    setFormError(null);

    try {
      const response = await authService.resetPassword(formData);
      
      if (response.success) {
        setResetSuccess(true);
      } else {
        setFormError(response.error || 'Произошла ошибка при сбросе пароля');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Ошибка при сбросе пароля';
      setFormError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200">
      <div className="fixed top-4 right-4">
        <button
          onClick={() => document.documentElement.classList.toggle('dark')}
          className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          aria-label="Toggle theme"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-800 dark:text-gray-200">
            <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path>
          </svg>
        </button>
      </div>
      <div className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8">
          <div className="mb-6">
            <button
              onClick={() => navigate('/auth/login')}
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="text-sm">Назад ко входу</span>
            </button>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Сброс пароля
          </h2>

          {formError && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mr-2" />
                <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
              </div>
            </div>
          )}

          {resetSuccess ? (
            <div className="text-center">
              <div className="mb-4 flex justify-center">
                <Check className="w-16 h-16 text-green-500 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                Пароль успешно изменен
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Ваш пароль был успешно изменен. Теперь вы можете войти в свою учетную запись с новым паролем.
              </p>
              <button
                onClick={() => navigate('/auth/login')}
                className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Перейти ко входу
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Новый пароль
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    className="w-full pl-10 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/25"
                    placeholder="Введите новый пароль"
                    required
                    minLength={8}
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Минимум 8 символов
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Подтверждение пароля
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full pl-10 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/25"
                    placeholder="Подтвердите новый пароль"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !formData.email || !formData.token}
                variant="primary"
                fullWidth
                className="flex items-center justify-center"
              >
                {isLoading ? 'Сохранение...' : 'Сохранить новый пароль'}
              </Button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Вспомнили пароль?{' '}
            <CustomLink
              to="/auth/login"
              className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              Вернуться ко входу
            </CustomLink>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword; 